<template>
  <v-dialog :value="value" max-width="520" @input="$emit('input', $event)">
    <v-card v-if="target" light class="ad-edit">
      <v-card-title class="ad-edit__title">
        <div>
          <div class="ad-edit__type">
            <span class="ad-edit__channel-badge" :class="`ad-edit__channel-badge--${target.channel}`">{{ channelBadge }}</span>
            {{ target.entityType === "campaign" ? "Kampagne bearbeiten" : "Anzeige bearbeiten" }}
          </div>
          <div class="ad-edit__name">{{ target.name }}</div>
          <div v-if="target.entityType === 'ad' && target.campaign" class="ad-edit__campaign">{{ target.campaign }}</div>
        </div>
      </v-card-title>

      <v-card-text class="pb-0">
        <v-alert v-if="recommendation" dense text color="primary" class="ad-edit__recommendation">
          <div class="ad-edit__recommendation-text">
            <v-icon small color="primary" class="mr-1">mdi-lightbulb-on-outline</v-icon>
            {{ recommendation.text }}
          </div>
          <v-btn x-small depressed color="primary" class="mt-1" @click="applyRecommendation">
            Empfehlung übernehmen
          </v-btn>
        </v-alert>

        <div class="ad-edit__field-label">Status</div>
        <v-btn-toggle v-model="form.status" mandatory dense class="mb-1">
          <v-btn small value="ACTIVE">
            <v-icon small left color="green darken-1">mdi-play-circle-outline</v-icon>
            Aktiv
          </v-btn>
          <v-btn small value="PAUSED">
            <v-icon small left color="grey darken-1">mdi-pause-circle-outline</v-icon>
            Pausiert
          </v-btn>
        </v-btn-toggle>
        <div v-if="parentPausedHint" class="ad-edit__hint warning--text text--darken-2">{{ parentPausedHint }}</div>

        <template v-if="canEditBudget">
          <div class="ad-edit__field-label mt-3">Tagesbudget</div>
          <v-text-field
            v-model="form.dailyBudget"
            dense
            outlined
            hide-details
            type="number"
            min="1"
            suffix="€ / Tag"
            class="ad-edit__number-field"
          />
          <div class="ad-edit__hint">Aktuell: {{ euro(target.dailyBudget) }}/Tag. Bei Fremdwährungs-Konten wird automatisch umgerechnet.</div>
        </template>

        <template v-if="canEditTargetRoas">
          <div class="ad-edit__field-label mt-3">Ziel-ROAS</div>
          <v-text-field
            v-model="form.targetRoas"
            dense
            outlined
            hide-details
            type="number"
            min="0.1"
            step="0.1"
            class="ad-edit__number-field"
          />
          <div class="ad-edit__hint">Aktuell: {{ number(target.targetRoas) }}<span v-if="breakEvenRoas"> · Break-even liegt bei ≈ {{ number(breakEvenRoas) }}</span></div>
        </template>

        <v-alert v-if="error" dense text type="error" class="mt-3 mb-0">{{ error }}</v-alert>
        <v-alert v-if="success" dense text type="success" class="mt-3 mb-0">
          Änderung gespeichert — die Plattform braucht ggf. wenige Minuten, bis sie überall sichtbar ist.
        </v-alert>
      </v-card-text>

      <v-card-actions class="px-6 pb-4 pt-3">
        <v-btn v-if="target.link" small text color="primary" :href="target.link" target="_blank">
          <v-icon small left>mdi-open-in-new</v-icon>
          {{ platformLabel }}
        </v-btn>
        <v-spacer />
        <v-btn small text @click="$emit('input', false)">{{ success ? "Schließen" : "Abbrechen" }}</v-btn>
        <v-btn small depressed color="primary" :disabled="!hasChanges" :loading="saving" @click="save">
          Speichern
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../../fb";
import { getDecimalNumberString } from "@/utils";

export default {
  name: "AdEditDialog",
  props: {
    value: Boolean,
    shopCode: String,
    target: Object,
    recommendation: Object,
    breakEvenRoas: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      form: { status: "ACTIVE", dailyBudget: null, targetRoas: null },
      saving: false,
      error: null,
      success: false,
    };
  },
  computed: {
    channelBadge() {
      return { facebook: "FB", google: "G", tiktok: "TT" }[this.target?.channel] || "?";
    },
    platformLabel() {
      return { facebook: "Im Ads Manager öffnen", google: "In Google Ads öffnen", tiktok: "In TikTok Ads öffnen" }[this.target?.channel];
    },
    // TikTok supports only status changes; budgets/target ROAS only exist on campaigns.
    canEditBudget() {
      return this.target?.entityType === "campaign" && this.target.dailyBudget !== null && this.target.channel !== "tiktok";
    },
    canEditTargetRoas() {
      return this.target?.entityType === "campaign" && !!this.target.targetRoas && this.target.channel === "google";
    },
    // Activating an ad inside a paused campaign/adset has no visible effect.
    parentPausedHint() {
      if (this.target?.entityType !== "ad" || this.form.status !== "ACTIVE") return null;
      if (this.target.status === "CAMPAIGN_PAUSED") return "Die Kampagne ist pausiert — die Anzeige läuft erst wieder, wenn auch die Kampagne aktiviert wird.";
      if (this.target.status === "ADSET_PAUSED") return "Die Anzeigengruppe ist pausiert — die Anzeige läuft erst wieder, wenn auch die Gruppe aktiviert wird.";
      return null;
    },
    changedUpdates() {
      if (!this.target) return {};
      const updates = {};
      if (this.form.status !== this.initialStatus) updates.status = this.form.status;
      if (this.canEditBudget && this.form.dailyBudget !== null && this.form.dailyBudget !== "") {
        const budget = +parseFloat(this.form.dailyBudget).toFixed(2);
        if (budget !== +this.target.dailyBudget.toFixed(2)) updates.dailyBudget = budget;
      }
      if (this.canEditTargetRoas && this.form.targetRoas !== null && this.form.targetRoas !== "") {
        const roas = +parseFloat(this.form.targetRoas).toFixed(2);
        if (roas !== +this.target.targetRoas.toFixed(2)) updates.targetRoas = roas;
      }
      return updates;
    },
    hasChanges() {
      return Object.keys(this.changedUpdates).length > 0 && !this.success;
    },
    initialStatus() {
      // Effective states like CAMPAIGN_PAUSED mean the entity itself may still be
      // ACTIVE — for ads only their own PAUSED counts as paused here.
      if (this.target?.entityType === "ad") return this.target.status === "PAUSED" ? "PAUSED" : "ACTIVE";
      return this.target?.status && this.target.status !== "ACTIVE" ? "PAUSED" : "ACTIVE";
    },
  },
  watch: {
    value(open) {
      if (open) this.reset();
    },
  },
  methods: {
    reset() {
      this.error = null;
      this.success = false;
      this.saving = false;
      this.form = {
        status: this.initialStatus,
        dailyBudget: this.target?.dailyBudget !== null && this.target?.dailyBudget !== undefined ? `${this.target.dailyBudget}` : null,
        targetRoas: this.target?.targetRoas ? `${this.target.targetRoas}` : null,
      };
    },
    applyRecommendation() {
      if (!this.recommendation) return;
      if (this.recommendation.status) this.form.status = this.recommendation.status;
      if (this.recommendation.dailyBudget) this.form.dailyBudget = `${this.recommendation.dailyBudget}`;
      if (this.recommendation.targetRoas) this.form.targetRoas = `${this.recommendation.targetRoas}`;
    },
    async save() {
      this.saving = true;
      this.error = null;
      try {
        const updateAdEntity = httpsCallable(functions, "updateAdEntity", { timeout: 120000 });
        await updateAdEntity({
          shopCode: this.shopCode,
          channel: this.target.channel,
          entityType: this.target.entityType,
          entityId: this.target.entityId,
          adGroupId: this.target.adGroupId || null,
          updates: this.changedUpdates,
        });
        this.success = true;
        this.$emit("saved");
      } catch (e) {
        this.error = e.message || "Änderung fehlgeschlagen.";
      }
      this.saving = false;
    },
    euro(value) {
      return getDecimalNumberString(value, 2) + " €";
    },
    number(value, fraction = 2) {
      return getDecimalNumberString(value, fraction);
    },
  },
};
</script>

<style lang="scss" scoped>
.ad-edit__title {
  padding-bottom: 8px;
}

.ad-edit__type {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #757575;
}

.ad-edit__name {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  word-break: break-word;
}

.ad-edit__campaign {
  font-size: 12px;
  color: #9e9e9e;
}

.ad-edit__channel-badge {
  font-size: 10px;
  font-weight: 700;
  border-radius: 4px;
  padding: 1px 5px;
  color: white;

  &--facebook {
    background-color: #1877f2;
  }

  &--google {
    background-color: #34a853;
  }

  &--tiktok {
    background-color: #010101;
  }
}

.ad-edit__recommendation {
  font-size: 13px;
}

.ad-edit__recommendation-text {
  display: flex;
  align-items: flex-start;
}

.ad-edit__field-label {
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 4px;
  color: #616161;
}

.ad-edit__number-field {
  max-width: 220px;
}

.ad-edit__hint {
  font-size: 11px;
  color: #9e9e9e;
  margin-top: 4px;
}
</style>
